import React, { useEffect } from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import PropTypes from 'prop-types';
import _ from 'lodash';
import { withStyles, IconButton, InputAdornment } from '@material-ui/core';
import TableRow from '@material-ui/core/TableRow';
import TableCell from '@material-ui/core/TableCell';
import Typography from '@material-ui/core/Typography';
import Button from '@material-ui/core/Button';
import TextField from '@material-ui/core/TextField';
import TablePagination from '@material-ui/core/TablePagination';
import Search from '@material-ui/icons/Search';
import Close from '@material-ui/icons/Close';
import LocationCity from '@material-ui/icons/LocationCity';
import MoreIcon from '@material-ui/icons/MoreVert';
import MenuItem from '@material-ui/core/MenuItem';
import Menu from '@material-ui/core/Menu';
import CircularProgress from '@material-ui/core/CircularProgress';
import Table from '../table/Table';

import './Firms.scss';
import {
  selectFirmsList,
  selectFirmsTotal,
  selectFirmsPageDetails,
} from './firmsSelectors';

import { fetch, setPageDetails } from './firmsActions';

const styles = {
  loadingSpinner: {
    position: 'absolute',
    left: '50%',
    top: '14%',
  },
  cell: {
    paddingTop: 0,
    paddingBottom: 0,
  },
};

const invertDirection = {
  asc: 'desc',
  desc: 'asc',
};

const NoResults = () => (
  <div className='no-results-container'>
    <div className='no-results-icon-container'>
      <LocationCity className='no-results-person' />
      <div className='no-results-search-container'>
        <Search className='no-results-search' />
      </div>
    </div>
    <div className='no-results-text1'>
      <Typography variant='h5'>No Firms found</Typography>
    </div>
    <div className='no-results-text2'>Try adjusting your search</div>
  </div>
);

const mapDispatchToProps = dispatch => ({
  fetchFirms: pageDetails => dispatch(fetch(pageDetails)),
  setPageDetails: pageDetails => dispatch(setPageDetails(pageDetails)),
});

const mapStateToProps = (state) => {
  return {
    data: selectFirmsList(state),
    totalFirms: selectFirmsTotal(state),
    firmsPageDetails: selectFirmsPageDetails(state),
    isLoading: _.get(state, 'firms.request.loading', false),
  };
};

export const Firms = ({
  data,
  classes,
  fetchFirms,
  setPageDetails,
  totalFirms,
  firmsPageDetails,
  isLoading,
  history,
}) => {
  const [menuAnchorEl, setMenuAnchorEl] = React.useState(null);
  const [selectedFirmId, setSelectedFirmId] = React.useState(null);

  const sortFieldByColumn = {
    'Firm ID': 'ExternalCompanyId',
    'Firm Name': 'FirmName',
    'Address': 'Address',
    'City': 'City',
    'State': 'State',
    'Zip': 'Zip',
  };

  const handleMenuOpen = (event, firm) => {
    setSelectedFirmId(firm.extrn_company_id);
    setMenuAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setSelectedFirmId(null);
    setMenuAnchorEl(null);
  };

  const handleEditFirm = rowData => {
    handleMenuClose();
    history.push(`/firms/${rowData.extrn_company_id}/edit`);
  };

  useEffect(() => {
    let pageDetails = {
      page: 0,
      rowsPerPage: 10,
      sortColumn: sortFieldByColumn['Firm ID'],
      sortDirection: 'desc',
      filterObject: { FirmName: '' },
      currentSortedColumnName: 'Firm ID',
    };
    setPageDetails(pageDetails);
    fetchFirms(pageDetails);
  }, []);

  const handleSearch = () => {
    let pageDetails = {
      page: 0,
      rowsPerPage: firmsPageDetails.rowsPerPage,
      sortColumn: firmsPageDetails.sortColumn,
      sortDirection: firmsPageDetails.sortDirection,
      filterObject: firmsPageDetails.filterObject,
    };
    setPageDetails(pageDetails);
    fetchFirms(pageDetails);
  };

  const handleChangeFirmName = event => {
    let pageDetails = {
      page: firmsPageDetails.page,
      rowsPerPage: firmsPageDetails.rowsPerPage,
      sortColumn: firmsPageDetails.sortColumn,
      sortDirection: firmsPageDetails.sortDirection,
      filterObject: {
        FirmName: event.target.value,
      },
    };
    setPageDetails(pageDetails);
  };

  const handleKeyPress = event => {
    const code = event.keyCode || event.which;
    if (code === 13) { //13 is the enter keycode
      handleSearch();
    }
  };

  const handleClear = () => {
    let pageDetails = {
      page: 0,
      rowsPerPage: 10,
      sortColumn: sortFieldByColumn['Firm ID'],
      sortDirection: 'desc',
      filterObject: {
        FirmName: '',
      },
      currentSortedColumnName: 'Firm ID',
    };
    setPageDetails(pageDetails);
    fetchFirms(pageDetails);
  };

  const columnHeaders = [
    'Firm ID',
    'Firm Name',
    'Address',
    'City',
    'State',
    'Zip',
    'Actions',
  ];

  function handleChangePage(event, newPage) {
    let pageDetails = {
      page: newPage,
      rowsPerPage: firmsPageDetails.rowsPerPage,
      sortColumn: firmsPageDetails.sortColumn,
      sortDirection: firmsPageDetails.sortDirection,
      filterObject: firmsPageDetails.filterObject,
      currentSortedColumnName: firmsPageDetails.currentSortedColumnName,
    };

    setPageDetails(pageDetails);
    fetchFirms(pageDetails);
  }

  function handleChangeRowsPerPage(event) {
    let pageDetails = {
      page: 0,
      rowsPerPage: +event.target.value,
      sortColumn: firmsPageDetails.sortColumn,
      sortDirection: firmsPageDetails.sortDirection,
      filterObject: firmsPageDetails.filterObject,
      currentSortedColumnName: firmsPageDetails.currentSortedColumnName,
    };

    setPageDetails(pageDetails);
    fetchFirms(pageDetails);
  }

  const handleSorting = column => {
    const currentSortedColumnName = column;
    const sortColumn = sortFieldByColumn[column];

    if (!sortColumn) {
      return;
    }

    let pageDetails = {
      page: firmsPageDetails.page,
      rowsPerPage: firmsPageDetails.rowsPerPage,
      sortColumn,
      sortDirection: invertDirection[firmsPageDetails.sortDirection],
      filterObject: firmsPageDetails.filterObject,
      currentSortedColumnName: currentSortedColumnName,
    };

    /* state update */
    setPageDetails(pageDetails);

    /* Action call */
    fetchFirms(pageDetails);
  };

  const renderTableRow = (rowData) => (
    <TableRow key={rowData.extrn_company_id} className="firm-row">
      <TableCell>{rowData.extrn_company_id}</TableCell>
      <TableCell>{rowData.extrn_company_name}</TableCell>
      <TableCell>{rowData.address_line_1}</TableCell>
      <TableCell>{rowData.city}</TableCell>
      <TableCell>{rowData.state}</TableCell>
      <TableCell>{rowData.zip}</TableCell>
      <TableCell className={classes.cell}>
        <IconButton onClick={e => handleMenuOpen(e, rowData)}>
          <MoreIcon />
        </IconButton>
        <Menu
          id="firm-actions-menu"
          anchorEl={menuAnchorEl}
          keepMounted
          open={Boolean(menuAnchorEl) && selectedFirmId === rowData.extrn_company_id}
          onClose={handleMenuClose}
          elevation={0}
          PaperProps={{
            style: {
              border: '1px solid #E0E0E0',
            },
          }}
        >
          <MenuItem onClick={() => handleEditFirm(rowData)}>Edit firm</MenuItem>
        </Menu>
      </TableCell>
    </TableRow>
  );

  return (
    <div className='firm'>
      <div className='firm-container'>
        <div className='title-area'>
          <Typography className='title-text'>Firms</Typography>
          {isLoading && <CircularProgress className={classes.loadingSpinner} />}
        </div>
        <div className='search-area'>
          <TextField
            className='search-input'
            variant='outlined'
            placeholder='Search..'
            value={firmsPageDetails.filterObject.FirmName}
            onChange={handleChangeFirmName}
            onKeyDown={handleKeyPress}
            InputProps={{
              endAdornment: (
                <InputAdornment position='end'>
                  <IconButton
                    onClick={handleSearch}
                    edge='start'
                    size='small'
                    style={{ marginRight: '4px' }}
                  >
                    <Search />
                  </IconButton>
                  {firmsPageDetails.filterObject.FirmName && (
                    <IconButton
                      onClick={handleClear}
                      edge='end'
                      size='small'
                    >
                      <Close />
                    </IconButton>
                  )}
                </InputAdornment>
              ),
              style: { padding: '9px' },
            }}
          />
        </div>
        <div className='firm-button-area'>
          <Button 
            className='firm-button'
            variant="contained"
            color="primary"
            onClick={() => history.push('/firms/create')}
          >
            Create New Firm
          </Button>
        </div>
        <div className="body-area">
          {data.length ? (
            <div className="table-container">
              <Table
                columnHeaders={columnHeaders}
                sortingHandler={handleSorting}
                sortDirection={firmsPageDetails.sortDirection}
                sortColumn={firmsPageDetails.currentSortedColumnName}>
                {_.map(data, rowData => renderTableRow(rowData))}
              </Table>
              <TablePagination
                rowsPerPageOptions={[10, 20, 50, 100]}
                component='div'
                count={totalFirms}
                rowsPerPage={firmsPageDetails.rowsPerPage}
                page={firmsPageDetails.page}
                backIconButtonProps={{
                  'aria-label': 'previous page',
                }}
                nextIconButtonProps={{
                  'aria-label': 'next page',
                }}
                onChangePage={handleChangePage}
                onChangeRowsPerPage={handleChangeRowsPerPage}
              />
            </div>
          ) : (
              <NoResults />
            )}
        </div>
      </div>
    </div>
  );
};

Firms.defaultProps = {
  data: [],
  totalFirms: 0,
  firmsPageDetails: {
    page: 0,
    rowsPerPage: 10,
    sortColumn: 'ExternalCompanyId',
    sortDirection: 'desc',
    filterObject: {
      FirmName: '',
    },
    currentSortedColumnName: 'Firm ID',
  },
  history: { push: () => { } },
  classes: {},
};

Firms.propTypes = {
  data: PropTypes.arrayOf(PropTypes.object),
  totalFirms: PropTypes.number,
  firmsPageDetails: PropTypes.shape({
    page: PropTypes.number.isRequired,
    rowsPerPage: PropTypes.number.isRequired,
    sortColumn: PropTypes.string.isRequired,
    sortDirection: PropTypes.string.isRequired,
    filterObject: PropTypes.shape({
      FirmName: PropTypes.string.isRequired,
    }).isRequired,
    currentSortedColumnName: PropTypes.string.isRequired,
  }),
  history: PropTypes.shape({
    push: PropTypes.func.isRequired,
  }),
  fetchFirms: PropTypes.func.isRequired,
  setPageDetails: PropTypes.func.isRequired,
  isLoading: PropTypes.bool.isRequired,
  classes: PropTypes.object,
};

const reduxConnected = connect(
  mapStateToProps,
  mapDispatchToProps,
)(withStyles(styles)(Firms));

export default withRouter(reduxConnected);

